import { isLmsWindow } from "@/lib/lms"
import { ThemeProvider } from "@/components/common/theme-provider"
import { Outlet, useLocation, useSearch } from "@tanstack/react-router"
import { useEffect, useRef } from "react"
import { useTempStore } from "@/lib/store"
import { Intercom, boot, shutdown } from "@/lib/cjs-interop"
import { ToasterWrapper } from "@/components/ToasterWrapper"
import { RouteTitleSync } from "@/components/PageHeader"
import { isMobile } from "react-device-detect"
import { trackEvent } from "@/lib/log.ts"
import ReactGA from "react-ga4"

const intercomSettings = {
  api_base: "https://api-iam.intercom.io",
  app_id: "dutnyhkk",
}

export function Root() {
  const { pathname, href } = useLocation()
  // `step` is the demo flow's current step, synced to the URL by DemoFlow.
  const {
    step: demoStep,
    utm_id: utmId,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_content: utmContent,
  } = useSearch({ strict: false }) as { step?: string } & Record<string, any>

  // Keep Intercom out of the way during the middle of the guided demo funnel —
  // its proactive pop-ups otherwise collide with the scenario briefing, the
  // lead-capture form, and the live call (ENG-1357). It stays available on the
  // intro/setup screens and returns on the feedback screen.
  const hideIntercomForDemoStep =
    (pathname === "/demo" || pathname.startsWith("/demo/")) &&
    !!demoStep &&
    ["scenario", "email", "call"].includes(demoStep)

  // Tracks whether we've shut Intercom down, so the re-show path knows an
  // explicit boot is required to restart the messenger.
  const intercomWasShutDownRef = useRef(false)

  useEffect(() => {
    if (isMobile) return
    // Don't boot Intercom when the app is embedded in an iframe (e.g. the demo
    // running inside an LMS via the SCORM package) — a support widget inside a
    // customer's course looks out of place. Only /demo is embeddable, so this
    // never affects the main app.
    if (window.parent !== window) return
    // ENG-1476: no support widget (or third-party script) in the LMS launch
    // window
    if (isLmsWindow()) return
    // if (import.meta.env.VITE_APP_ENV === "development") return
    if (hideIntercomForDemoStep) {
      // Tear Intercom down (covers the case where it was booted on an earlier
      // demo/main-app screen before the user reached these steps). Deep-linking
      // straight into one of these steps means there's nothing to shut down —
      // skip so the re-show path doesn't issue a redundant boot later.
      if (window.Intercom) {
        shutdown()
        intercomWasShutDownRef.current = true
      }
    } else {
      // Injects the widget script and boots the messenger on first run; on
      // subsequent runs the SDK only issues an "update" under the hood.
      Intercom(intercomSettings)
      if (intercomWasShutDownRef.current) {
        // After a shutdown, an "update" does not restart the messenger — per
        // the Intercom docs an explicit boot is required to bring it back.
        boot(intercomSettings)
        intercomWasShutDownRef.current = false
      }
    }
  }, [hideIntercomForDemoStep])

  const { user } = useTempStore()

  // Store UTM parameters in localStorage on first visit
  useEffect(() => {
    // Only store if we have UTM data and haven't stored it before
    if (utmSource && !localStorage.getItem("initialUtmData")) {
      const utmData = {
        utmId,
        utmSource,
        utmMedium,
        utmCampaign,
        utmTerm,
        utmContent,
        timestamp: Date.now(),
      }
      localStorage.setItem("initialUtmData", JSON.stringify(utmData))
    }
  }, [utmId, utmSource, utmMedium, utmCampaign, utmTerm, utmContent])

  useEffect(() => {
    trackEvent({
      eventName: "viewed a page",
      userId: user?.id,
      properties: {
        path: pathname,
        utmSource,
        utmMedium,
        utmCampaign,
        utmId,
        utmTerm,
        utmContent,
      },
    })
  }, [
    pathname,
    user,
    utmSource,
    utmMedium,
    utmCampaign,
    utmId,
    utmTerm,
    utmContent,
  ])

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      title: pathname,
    })
  }, [pathname])

  const firstRender = useRef(true)
  useEffect(() => {
    if (import.meta.env.VITE_APP_ENV === "development" && firstRender.current) {
      const favicon = document.querySelector("link[rel~='icon']")
      if (favicon) {
        favicon.setAttribute("href", "https://example.com")
      }
      firstRender.current = false
    }
  }, [])

  return (
    <>
      <RouteTitleSync />
      <ToasterWrapper />
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <div className="!text-gray-600 h-full overflow-auto">
          <Outlet />
        </div>
      </ThemeProvider>
    </>
  )
}
